import { useState, useRef, useLayoutEffect } from 'react';
import { Icons } from './ui-atoms.jsx';

// Auto-grow cap for the composer textarea. ~6 lines at 13px / 1.5 line-
// height — generous enough for the demo seed (5 lines) plus a line of
// breathing room, while still keeping the chat region above the
// composer comfortably visible on a 844px device.
const COMPOSER_MAX_HEIGHT_PX = 132;

/**
 * Live composer — input + send button. Defaults to uncontrolled
 * (owns its own text). Pass `value` + `onChange` to use controlled
 * mode, where the parent owns the text — that's what T6's
 * preview-on-send needs so AppShell can clear the composer on
 * commit but preserve it on cancel ("Edit message").
 *
 * Auto-clear behavior is the same in both modes: after a successful
 * onSend (result !== null), the text is cleared. `null` is the hook's
 * documented "in-flight, dropped" return; preserving the text lets
 * the participant retry without retyping. Under a deferred Send Policy
 * preview, the policy returns null specifically so the composer holds the
 * draft until confirm/cancel resolves.
 *
 * This is the PLAIN composer — no privacy UI. It renders only when F2
 * is disabled (Phase-0 baseline, F1/F3/F4 rows); the F2 composer with
 * inline at-risk hints and the D4 action column lives in
 * annotated-composer.jsx.
 *
 * @param {object} props
 * @param {(text: string) => unknown | Promise<unknown>} props.onSend
 * @param {string}  [props.value]        controlled text (omit for uncontrolled)
 * @param {(text: string) => void} [props.onChange]
 * @param {boolean} [props.disabled]     true when a send is in flight
 * @param {string}  [props.placeholder]
 */
export function Composer({ onSend, value, onChange, disabled = false, placeholder = 'Message…' }) {
  const isControlled = value !== undefined;
  const [internal, setInternal] = useState('');
  const text = isControlled ? value : internal;
  const setText = (next) => {
    if (isControlled) onChange?.(next);
    else setInternal(next);
  };
  const canSend = !disabled && text.trim().length > 0;

  // Auto-grow: the textarea's height tracks its own scrollHeight on
  // every text change, capped at COMPOSER_MAX_HEIGHT_PX. Reset to
  // 'auto' first so a delete-key shrink also triggers re-measure (the
  // browser caches scrollHeight from the previous bigger size otherwise
  // and the textarea stays at its peak height forever).
  // useLayoutEffect rather than useEffect so the resize is applied
  // before paint — prevents the jitter you'd see if the textarea
  // briefly rendered at its previous height before snapping.
  const textareaRef = useRef(null);
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_HEIGHT_PX)}px`;
  }, [text]);

  const submit = async () => {
    if (!canSend) return;
    try {
      const result = await onSend(text);
      if (result !== null) setText('');
    } catch {
      // Keep the text on the screen so the user can retry.
    }
  };

  const onKeyDown = (e) => {
    // Don't fire mid-IME composition. CJK pinyin commits with Enter; an
    // unguarded handler would send half-composed Chinese as a message.
    if (e.isComposing || e.keyCode === 229) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div
      style={{
        padding: '8px 10px',
        background: 'var(--paper-2)',
        borderTop: '1px solid var(--line)',
        display: 'flex',
        alignItems: 'flex-end',
        gap: 8,
      }}
    >
      <button
        type="button"
        aria-label="Attach"
        disabled={disabled}
        style={{
          flexShrink: 0,
          background: 'var(--paper)',
          border: '1px solid var(--line)',
          borderRadius: 999,
          width: 34,
          height: 34,
          cursor: disabled ? 'default' : 'pointer',
          display: 'grid',
          placeItems: 'center',
          color: 'var(--ink-soft)',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {Icons.plus(16, 'currentColor')}
      </button>

      <textarea
        ref={textareaRef}
        rows={1}
        value={text}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        style={{
          flex: 1,
          background: 'var(--paper)',
          border: '1px solid var(--line)',
          borderRadius: 18,
          padding: '8px 12px',
          fontFamily: 'inherit',
          fontSize: 13,
          lineHeight: 1.4,
          color: 'var(--ink)',
          outline: 'none',
          // Vertical resize is driven by the useLayoutEffect above;
          // the user dragging the manual resize handle would fight
          // the auto-grow. Disable both axes.
          resize: 'none',
          // Wrap rules — lets long demo seed render across multiple
          // lines instead of horizontally truncating.
          whiteSpace: 'pre-wrap',
          overflowWrap: 'break-word',
          // Internal scroll only kicks in once the auto-grow hits the
          // cap (COMPOSER_MAX_HEIGHT_PX); below that, scrollHeight
          // drives the height and overflow stays hidden.
          overflowY: 'auto',
          maxHeight: COMPOSER_MAX_HEIGHT_PX,
        }}
      />

      <button
        type="button"
        aria-label="Send"
        disabled={!canSend}
        onClick={() => {
          void submit();
        }}
        style={{
          flexShrink: 0,
          background: canSend ? 'var(--indigo)' : 'var(--paper-3)',
          border: 'none',
          borderRadius: 999,
          width: 34,
          height: 34,
          cursor: canSend ? 'pointer' : 'default',
          display: 'grid',
          placeItems: 'center',
          boxShadow: canSend ? '0 2px 6px oklch(45% 0.11 265 / 0.3)' : 'none',
        }}
      >
        {Icons.send(16, canSend ? 'var(--paper)' : 'var(--ink-faint)')}
      </button>
    </div>
  );
}
